import { useEffect, useState } from "react";
import { Navigate, Link, useParams } from "react-router-dom";
import { QrCode } from "lucide-react";
import { api } from "@/lib/api";

const decode = (value) => {
  try {
    return decodeURIComponent(value || "");
  } catch {
    return value || "";
  }
};

/** Short QR target: /q/<PROJECT CODE|projectId>/<BLOCK NAME> -> Panel Properties glass view. */
export default function QrRedirectPage() {
  const { projectRef, blockName } = useParams();
  const isId = /^[0-9a-f]{24}$/i.test(projectRef || "");
  const [projectId, setProjectId] = useState(isId ? projectRef.toLowerCase() : null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    if (isId) return;
    api
      .get(`/projects/by-code/${encodeURIComponent(decode(projectRef))}`)
      .then(({ data }) => setProjectId(data.id))
      .catch((error) =>
        setFailure(
          error?.response?.status === 403
            ? "You do not have access to this project."
            : "This QR code does not match any project."
        )
      );
  }, [isId, projectRef]);

  if (failure)
    return (
      <div className="grid h-screen place-items-center bg-[#EEF2F8] px-6">
        <div
          data-testid="qr-redirect-error"
          className="max-w-sm rounded-2xl border border-slate-200 bg-white px-7 py-8 text-center"
        >
          <QrCode size={24} className="mx-auto text-slate-400" />
          <p className="mono mt-4 text-[10.5px] uppercase tracking-[0.18em] text-slate-500">
            {decode(projectRef)} · {decode(blockName)}
          </p>
          <p className="mt-3 text-sm text-slate-700">{failure}</p>
          <Link
            to="/"
            className="mono mt-5 inline-block rounded-full bg-cyan-600 px-5 py-2 text-[10.5px] uppercase tracking-wider text-white hover:bg-cyan-500"
          >
            Back to projects
          </Link>
        </div>
      </div>
    );

  if (!projectId)
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF2F8]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    );

  return (
    <Navigate
      to={`/projects/${projectId}/properties?block=${encodeURIComponent(decode(blockName))}`}
      replace
    />
  );
}
